import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BlobServiceClient } from "@azure/storage-blob";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/react";

import { useToaster } from "../../common/hooks";
import i18next from "../../common/i18n";
import { retryWithBackoff } from "../../common/lib/utils";
import sagaUtils from "../../Store/audit-upload/sagaUtils";
import actions from "../../Store/actions";

const containerName = "audit-data";

function getFormattedNow() {
    const now = new Date();
    const formattedDate = `${now.getDate().toString().padStart(2, "0")}_${(
        now.getMonth() + 1
    )
        .toString()
        .padStart(2, "0")}_${now.getFullYear()}_${now
        .getHours()
        .toString()
        .padStart(2, "0")}_${now.getMinutes().toString().padStart(2, "0")}`;

    return formattedDate;
}

function generateSimpleHash() {
    const chars =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let hash = "";
    for (let i = 0; i < 5; i++) {
        hash += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return hash;
}

const prepareExport = i18next.t("messages.preparingExport");

const useAuditUpload = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const toast = useToaster();

    const { auditUpload, audits } = useSelector((state) => state);

    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadMessage, setUploadMessage] = useState(prepareExport);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [showDownload, setShowDownload] = useState(false);

    const generateAndUploadJSON = async (auditId) => {
        setShowDownload(false);
        setUploadMessage(prepareExport);
        for (const type of ["assetAuditor", "altAsset"]) {
            try {
                const jsonData = await exportIndexedDBToJSON(
                    "keyval-store",
                    type,
                    auditId
                );

                if (jsonData) {
                    const fileBlob = new Blob([jsonData], {
                        type: "application/json",
                    });
                    const formattedDate = getFormattedNow();
                    const fileName = `${type}_${formattedDate}_${generateSimpleHash()}.json`;

                    // Create a download link in case upload fails
                    const url = URL.createObjectURL(fileBlob);
                    setDownloadUrl(url);

                    await handleFileUpload(fileBlob, fileName, type, auditId);
                }
            } catch (error) {
                console.error("JSON export failed:", error);
                toast.error("messages.uploadFailed");
                setUploadMessage(`❌ ${t("messages.uploadFailed")}`);
                throw error;
            }
        }
    };

    // Function to get SAS token with retry logic
    const getSasTokenWithRetry = async (containerName) => {
        return retryWithBackoff(async () => {
            return await dispatch(
                actions.auth.create.getSasToken(containerName)
            );
        });
    };

    const handleFileUpload = async (file, fileName, type, auditId) => {
        if (!file) return;

        setUploadProgress(0);
        setUploadMessage(`Uploading audit id:${auditId} / ${type}...`);

        let response = null;
        try {
            response = await getSasTokenWithRetry(containerName);
        } catch (error) {
            console.error("Failed to get blob SAS token:", error);
            toast.error("Failed to get blob SAS token", error);
            Sentry.captureException(`Failed to get blob SAS token: ${error}`);
            setUploadMessage(`❌ ${t("messages.uploadFailed")}`);
            setShowDownload(true);
            throw error;
        }

        const { sasToken, accountName } = response;
        const blobSasUrl = `https://${accountName}.blob.core.windows.net/?${sasToken}`;

        try {
            const blobServiceClient = new BlobServiceClient(blobSasUrl);

            const containerClient =
                blobServiceClient.getContainerClient(containerName);
            const blockBlobClient = containerClient.getBlockBlobClient(
                `audit-exports/pending-submission/${auditId}/${fileName}`
            );

            const blockSize = 4 * 1024 * 1024; // 4MB per block
            const totalBlocks = Math.ceil(file.size / blockSize);
            let uploadedBytes = 0;
            let blockIds = [];

            const MAX_RETRIES = 3;

            for (let i = 0; i < totalBlocks; i++) {
                const start = i * blockSize;
                const end = Math.min(start + blockSize, file.size);
                const chunk = file.slice(start, end);
                const blockId = btoa(i.toString().padStart(6, "0")); // Unique block ID

                let attempt = 0;
                let uploaded = false;

                while (attempt < MAX_RETRIES && !uploaded) {
                    try {
                        setUploadMessage(
                            `Uploading audit id ${auditId} / ${type} block ${
                                i + 1
                            } of ${totalBlocks}...`
                        );

                        await blockBlobClient.stageBlock(
                            blockId,
                            chunk,
                            chunk.size
                        );
                        uploaded = true;
                    } catch (error) {
                        console.log("error", error);
                        attempt++;
                        console.warn(
                            `Block ${i} failed (Attempt ${attempt}/${MAX_RETRIES}):`,
                            error.message
                        );

                        if (attempt === 2) {
                            setUploadMessage(
                                `⚠️ ${t("messages.unstableConnection")}`
                            );
                        }

                        if (attempt === MAX_RETRIES) {
                            setUploadMessage(
                                `❌ Upload failed at block ${i}. Aborting.`
                            );
                            toast.error(
                                `Upload failed at block ${i}. Aborting.`
                            );
                            Sentry.captureException(
                                `Upload failed at block ${i}. Aborting audit:${auditId}`
                            );
                            setShowDownload(true);
                            throw error; // Abort upload
                        }

                        await new Promise((resolve) =>
                            setTimeout(resolve, attempt * 2000)
                        );
                    }
                }

                blockIds.push(blockId);
                uploadedBytes += chunk.size;
                const percentCompleted = Math.round(
                    (uploadedBytes / file.size) * 100
                );

                setUploadProgress(percentCompleted);
            }

            await blockBlobClient.commitBlockList(blockIds);

            setUploadMessage(`✅ ${t("messages.uploadSuccessful")}`);
        } catch (error) {
            console.error("Upload failed:", error.message);
            setUploadMessage(`❌ ${t("messages.uploadFailed")}`);
            toast.error(t("messages.uploadFailed"));
            Sentry.captureException(`Upload failed: ${error.message}`);
            setShowDownload(true);
            throw error;
        }
    };

    const convertKeysToSnakeCase = (obj) => {
        if (typeof obj !== "object" || obj === null) return obj;

        if (Array.isArray(obj)) {
            return obj.map(convertKeysToSnakeCase);
        }

        return Object.keys(obj).reduce((acc, key) => {
            let snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
            snakeKey = snakeKey.replace(/([a-zA-Z])(\d)/g, "$1_$2");
            acc[snakeKey] = convertKeysToSnakeCase(obj[key]);
            return acc;
        }, {});
    };

    const exportIndexedDBToJSON = (dbName, assetType, auditId) => {
        return new Promise((resolve, reject) => {
            const dbRequest = indexedDB.open(dbName);

            dbRequest.onsuccess = async (event) => {
                const db = event.target.result;
                const transaction = db.transaction(
                    db.objectStoreNames,
                    "readonly"
                );
                const data = {};

                for (const storeName of db.objectStoreNames) {
                    data[storeName] = await getAllDataWithKeys(
                        transaction,
                        storeName
                    );
                }

                const { keyval } = data;
                let assignment = null;
                let assetAuditor = [];
                let altAssets = [];

                for (const item of keyval) {
                    switch (item.key) {
                        case "assetAuditor":
                            assetAuditor = item.value.filter(
                                (asset) =>
                                    asset.auditAssignment.auditLocation.audit
                                        .id === parseInt(auditId)
                            );
                            break;

                        case "auditAssignment":
                            assignment = item.value.find(
                                (val) =>
                                    val.auditLocation.audit.id ===
                                    parseInt(auditId)
                            );
                            break;

                        case "altAsset":
                            if (assetType === "altAsset") {
                                altAssets = item.value.filter(
                                    (asset) =>
                                        asset.audit.id === parseInt(auditId)
                                );
                            }
                            break;
                    }
                }

                const auditSignature =
                    auditUpload[auditId]?.auditSignature || "";

                const firstAssetChecked = auditUpload[auditId]
                    ? auditUpload[auditId].firstAssetChecked
                    : "";

                const result = {
                    isSync: 0,
                    createdDate: new Date().toISOString(),
                    auditSignature: { ...auditSignature },
                    firstAssetChecked,
                    audit: auditId,
                };

                if (assetType === "altAsset") {
                    const res = await Promise.all(
                        altAssets.map(sagaUtils.mapAltAsset)
                    );
                    result.altAssetContent = res;
                    // do not upload empty altAsset blob
                    if (result.altAssetContent.length === 0) resolve(null);
                } else {
                    const res = await Promise.all(
                        assetAuditor.map(sagaUtils.mapAssetAuditor)
                    );

                    result.auditAssetContent = res;
                    result.auditAssignment = assignment?.id;
                }

                const jsonData = JSON.stringify(
                    convertKeysToSnakeCase(result),
                    null,
                    2
                );

                resolve(jsonData);
            };

            dbRequest.onerror = (event) => {
                console.error("Error opening IndexedDB:", event.target.error);
                Sentry.captureException(
                    `Error opening IndexedDB: ${event.target.error}`
                );
                reject(event.target.error);
            };
        });
    };

    const getAllDataWithKeys = (transaction, storeName) => {
        return new Promise((resolve, reject) => {
            const store = transaction.objectStore(storeName);
            const request = store.openCursor();
            const results = [];

            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    results.push({ key: cursor.key, value: cursor.value });
                    cursor.continue();
                } else {
                    resolve(results);
                }
            };

            request.onerror = () => reject(request.error);
        });
    };

    return {
        generateAndUploadJSON,
        uploadProgress,
        uploadMessage,
        showDownload,
        downloadUrl,
    };
};

export default useAuditUpload;
